import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

export default function Hero() {
  const { t } = useTranslation()
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 })

  return (
    <section className="pt-32 pb-20 hero-gradient overflow-hidden" id="accueil">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:w-1/2 space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-semibold text-sm"
            >
              <span className="material-icons text-sm mr-2">verified</span>
              {t('hero.badge')}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="text-5xl lg:text-7xl font-bold leading-tight"
            >
              {t('hero.title')}{' '}
              <span className="text-primary">{t('hero.titleHighlight')}</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="text-xl text-slate-600 dark:text-slate-400 max-w-xl"
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/solutions"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-primary text-white rounded-xl font-bold text-base sm:text-lg hover:scale-105 transition-transform shadow-xl shadow-primary/30"
              >
                {t('hero.ctaPrimary')}
              </Link>
              <Link
                to="/a-propos"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-base sm:text-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('hero.ctaSecondary')}
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:w-1/2 relative"
          >
            <motion.div
              animate={{
                y: [0, -20, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="relative z-10"
            >
              <img
                alt="High-tech kiosk and vending machine"
                className="rounded-3xl shadow-2xl border-4 border-white dark:border-slate-800"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuBXd60L7tsNrx4AYayC6hmW3VW40KORwDzlSjaqTc9EOTaphpHCz-KInP_GzfWCJYGPrAAkkgqUkQmpFh_HC6Oj_2DZTgCzT3bhdoIJYqz9g6YMfAqF141TQAn0B-FtQZ404av0OfZnYm2aImsfyn6R9uN-bDk8qmPi2xwRnAKMqsrAjoA7ZLDfNuFOlAyFfWivquMD_OUcoLcYFk-RhKXkdM2Xo7hg8REFQfgq13urnyzVVV3M7Xw0Ch963IwiA1fg1TqGDwOJnw"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.8 }}
                className="absolute -bottom-6 -left-6 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-xl flex items-center gap-4 border border-slate-100 dark:border-slate-700"
              >
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white">
                  <span className="material-icons">sentiment_very_satisfied</span>
                </div>
                <div>
                  <p className="font-bold text-lg">+98%</p>
                  <p className="text-sm text-slate-500">{t('hero.satisfaction')}</p>
                </div>
              </motion.div>
            </motion.div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/10 blur-3xl rounded-full -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
